import {motion} from "framer-motion";
import styled, {css, keyframes} from "styled-components";
import {filePanelFrame, filePanelSurface} from "components/FilePanel.styles";
import {fileKindPalette, type FileKindTone} from "download/fileKind";
import {fontFamilies} from "theme/tokens";
import {Summary} from "./FileSummary.styles";

/**
 * Externalized styles for {@link ./ProgressCard} (design `03-state`; rounded gauge in ui#942, a full
 * ring since kitecorp/transferify-ui#2888).
 *
 * Stage reuses Summary's flex layout but keeps the shared filePanelFrame rather than the composer's
 * personalization wash. The file strip's fade reads that same surface so the two stay consistent.
 * The large SVG ring owns the visual hierarchy; the scrolling file strip remains subordinate.
 * Every colour reads `theme.tokens`, so light/dark and the reduced-motion static presentation share
 * one definition.
 */

/** Every ancestor down to the payload box must remain a shrinkable flex column. */
export const UploadRoot = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
`;

export const Head = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 14px;
	flex: none;
`;

export const Title = styled.div`
	font-weight: 700;
	font-size: 18px;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
`;

/**
 * The line under the title (kitecorp/transferify-ui#1301, ui#3204): keep this tab open, or what the
 * connection is doing while the upload retries. Said as a polite live region by the card.
 */
export const UploadNote = styled.p`
	margin: -6px 0 12px;
	font-size: 12.5px;
	line-height: 1.45;
	color: ${({theme}) => theme.tokens.ink2};
	flex: none;
	overflow-wrap: anywhere;
`;

export const CancelButton = styled.button`
	background: ${({theme}) => theme.tokens.field};
	border: none;
	color: ${({theme}) => theme.tokens.ink2};
	font: 600 13px ${fontFamilies.sans};
	padding: 8px 14px;
	border-radius: 10px;
	cursor: pointer;
	transition: 0.14s;

	&:hover {
		background: ${({theme}) => theme.tokens.line};
		color: ${({theme}) => theme.tokens.ink};
	}
`;

/**
 * The payload box in its uploading state, on the plain panel surface (washes removed in ui#1840).
 *
 * Only the composing state uses the sender's accent wash. Keep the upload's existing shared frame
 * explicitly rather than inheriting the composer's borderless treatment through Summary.
 */
export const Stage = styled(Summary)<{$filesOnly?: boolean}>`
	${filePanelFrame};
	position: relative;
	overflow: hidden;
	min-height: 300px;
	padding: 14px;

	/* Holding only the per-file rows, under the ring that now sits on the card (kitecorp/transferify-ui#2888):
	   as tall as its rows. */
	${({$filesOnly}) => $filesOnly && css`
		min-height: 0;
		margin-top: 12px;
		padding: 10px 14px;
	`}
`;

export const StageContent = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
`;

/* ---- large overall gauge ---- */

export const Gauge = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	flex: none;
	width: 100%;
`;

/**
 * The ring's box: square, and as wide as the payload box allows up to a size that leaves the file strip
 * under it (owner decision 2026-09-25, kitecorp/transferify-ui#2888: a full circle, not a semicircle).
 */
export const GaugeVisual = styled.div`
	position: relative;
	width: min(100%, 232px);
	margin: 4px auto 0;
`;

export const GaugeSvg = styled.svg.attrs({
	viewBox: "0 0 200 200",
	preserveAspectRatio: "xMidYMid meet",
})`
	display: block;
	width: 100%;
	height: auto;
	overflow: visible;
`;

/**
 * One full turn from 12 o'clock, clockwise: two half-circle arcs, because a single arc cannot join a
 * point to itself. A path rather than a rotated circle, so `pathLength` starts at the top with no
 * transform to keep in step.
 */
const RING_PATH = "M 100 10 A 90 90 0 1 1 100 190 A 90 90 0 1 1 100 10";
const RING_STROKE = 14;

export const GaugeTrack = styled.path.attrs({d: RING_PATH})`
	fill: none;
	stroke: ${({theme}) => theme.tokens.fieldLine};
	stroke-width: ${RING_STROKE};
`;

/**
 * Solid stroke — the blue→teal gradient paint server was removed in ui#1840. `$color` is the sender's
 * accent when it reads on the card, else the progress role (`progressArcColor`, kitecorp/transferify-ui#2888):
 * blue, or a signed-in Premium account's colour shaded until it clears 3:1 (kitecorp/transferify-ui#2935).
 */
export const GaugeProgressArc = styled(motion.path).attrs({d: RING_PATH})<{$color: string}>`
	fill: none;
	stroke: ${({$color}) => $color};
	stroke-width: ${RING_STROKE};
	stroke-linecap: round;
`;

/** The percentage, and under it the time left and the bytes sent, centred inside the ring. */
export const GaugeCenter = styled.div`
	position: absolute;
	inset: 18%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 4px;
	min-width: 0;
	text-align: center;
	color: ${({theme}) => theme.tokens.ink};
`;

export const GaugePercent = styled.span`
	font-weight: 700;
	font-size: clamp(36px, 9vw, 50px);
	line-height: 0.95;
	letter-spacing: -0.045em;
	font-variant-numeric: tabular-nums;
	margin-bottom: 4px;
`;

/** `ink2`: the small lines under the percentage, which must still clear 4.5:1 on the panel surface. */
export const GaugeDetail = styled(motion.span)`
	max-width: 100%;
	font-size: 12px;
	font-weight: 600;
	line-height: 1.25;
	color: ${({theme}) => theme.tokens.ink2};
	font-variant-numeric: tabular-nums;
	overflow-wrap: anywhere;
`;

/* ---- the one-line gauge above Byte Runner (kitecorp/transferify-ui#2872) ---- */

/** While the game is open the gauge shrinks to this line: percent, size and time left. */
export const CompactGauge = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
	flex: none;
	margin-bottom: 12px;
	min-width: 0;
`;

export const CompactPercent = styled.span`
	font-weight: 700;
	font-size: 30px;
	letter-spacing: -0.04em;
	line-height: 1;
	color: ${({theme}) => theme.tokens.ink};
	font-variant-numeric: tabular-nums;
`;

export const CompactMeta = styled.span`
	min-width: 0;
	text-align: right;
	font-size: 13px;
	color: ${({theme}) => theme.tokens.ink2};
	font-variant-numeric: tabular-nums;
	overflow-wrap: anywhere;
`;

/** Space between the gauge and the Play offer under it. */
export const GameOfferSlot = styled.div`
	margin-top: 12px;
	flex: none;
`;

/* ---- narrow per-file meters ---- */

export const MeterTrack = styled.div<{$height: number; $marginTop: number}>`
	height: ${({$height}) => $height}px;
	border-radius: 999px;
	background: ${({theme}) => theme.tokens.fieldLine};
	overflow: hidden;
	margin-top: ${({$marginTop}) => $marginTop}px;
	flex: none;
`;

/** `$color`: the ring's colour (`progressArcColor`), so the file meters match it. */
export const MeterFill = styled.div<{$color: string}>`
	height: 100%;
	border-radius: 999px;
	background: ${({$color}) => $color};
	transition: width 0.35s ease;

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

/**
 * The space between two file rows. Exported because a windowed list (kitecorp/transferify-ui#2942) has to
 * know it to place the rows it draws.
 */
export const QUIET_LIST_ROW_GAP_PX = 6;

/** A {@link FileRow}'s height before a windowed list measures it: the 28px kind thumb sets it. */
export const QUIET_LIST_ROW_HEIGHT_ESTIMATE_PX = 28;

/** File identity stays visible without competing with the gauge. */
/** `$flush`: first in its box (under the ring's own box, ui#2888), so no gap or fade above the first row. */
export const QuietList = styled.div<{$flush?: boolean}>`
	display: flex;
	flex-direction: column;
	gap: ${QUIET_LIST_ROW_GAP_PX}px;
	margin-top: auto;
	padding-top: 16px;
	flex: 0 1 auto;
	min-height: 0;
	max-height: 104px;
	overflow-y: auto;
	/* Top fade for the scroll, painted in the panel's own surface so it dissolves into it (ui#1167). */
	background: linear-gradient(180deg, transparent 0%, ${filePanelSurface} 16px);
	overflow-x: hidden;

	${({$flush}) => $flush && css`
		margin-top: 0;
		padding-top: 0;
		background: none;
	`}
`;

export const FileRow = styled.div`
	display: flex;
	align-items: center;
	gap: 9px;
	flex: none;
`;

export const ThumbSm = styled.div<{$tone: FileKindTone}>`
	width: 28px;
	height: 28px;
	border-radius: 8px;
	flex: none;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 700;
	font-size: 9px;
	letter-spacing: 0.02em;
	color: ${({theme, $tone}) => fileKindPalette(theme.tokens, $tone).ink};
	background: ${({theme, $tone}) => fileKindPalette(theme.tokens, $tone).surface};
`;

export const FileInfo = styled.div`
	flex: 1;
	min-width: 0;
`;

export const FileTop = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: 8px;
`;

export const FileName = styled.span`
	font-weight: 600;
	font-size: 12.5px;
	color: ${({theme}) => theme.tokens.ink};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

/**
 * `ink2` for the same measured reason as {@link GaugeMetricLabel}: the tertiary ink renders around
 * 2.4:1 on the panel surface, which is not a readable percentage at 11px. It stays subordinate to
 * the file name through size and weight rather than through ink that cannot be read.
 */
export const FilePercent = styled.span`
	font-size: 11px;
	color: ${({theme}) => theme.tokens.ink2};
	font-weight: 600;
	flex: none;
	font-variant-numeric: tabular-nums;
`;

/* ---- instant-mode indeterminate (brief registration flash) ---- */

export const InstantTitle = styled.div`
	font-weight: 700;
	font-size: 20px;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
`;

export const InstantSub = styled.div`
	margin-top: 6px;
	font-size: 13px;
	color: ${({theme}) => theme.tokens.ink2};
`;

const slide = keyframes`
	0% { transform: translateX(-60%); }
	100% { transform: translateX(260%); }
`;

const breathe = keyframes`
	0%, 100% { opacity: 0.45; }
	50% { opacity: 1; }
`;

export const IndeterminateFill = styled(MeterFill)`
	width: 38%;
	animation: ${slide} 1.2s ease-in-out infinite;

	@media (prefers-reduced-motion: reduce) {
		width: 100%;
		animation: ${breathe} 2s ease-in-out infinite;
	}
`;

